import { Banknote, Package, Receipt, Truck } from 'lucide-react';

import { Skeleton } from '@/components/ui/skeleton';
import { cn, formatCurrencyAdaptive, formatNumber } from '@/lib/utils';

import type { FinancialSummary } from '../dashboard.api';

interface FinancialSummaryCardsProps {
  data: FinancialSummary | undefined;
  loading: boolean;
  className?: string;
}

interface CeldaProps {
  icon: typeof Truck;
  label: string;
  value: string;
  hint: string;
  tone: 'brand' | 'amber' | 'emerald' | 'ink';
}

const TONES: Record<CeldaProps['tone'], string> = {
  brand: 'bg-brand-50 text-brand-600',
  amber: 'bg-amber-50 text-amber-600',
  emerald: 'bg-emerald-50 text-emerald-600',
  ink: 'bg-ink-100 text-ink-600',
};

function Celda({ icon: Icon, label, value, hint, tone }: CeldaProps) {
  return (
    <div className="flex items-start gap-3 bg-white px-4 py-3.5">
      <span className={cn('flex size-9 shrink-0 items-center justify-center rounded-xl', TONES[tone])}>
        <Icon className="size-4.5" aria-hidden="true" />
      </span>

      <div className="min-w-0">
        <p
          title={value}
          className="truncate font-mono text-base font-bold tracking-tight text-ink-900 tabular-nums"
        >
          {value}
        </p>
        <p className="truncate text-xs text-ink-500">{label}</p>
        <p className="line-clamp-2 text-[11px] text-ink-400">{hint}</p>
      </div>
    </div>
  );
}

/** Compras, gastos y efectivo pendiente de depositar del rango. */
export function FinancialSummaryCards({ data, loading, className }: FinancialSummaryCardsProps) {
  if (loading) {
    return (
      <div className={cn('grid gap-px overflow-hidden rounded-card border border-ink-100 bg-ink-100 sm:grid-cols-2 xl:grid-cols-4', className)}>
        {Array.from({ length: 4 }).map((_, index) => (
          <Skeleton key={index} className="h-20 rounded-none" />
        ))}
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className={cn('grid gap-px overflow-hidden rounded-card border border-ink-100 bg-ink-100 sm:grid-cols-2 xl:grid-cols-4', className)}>
      <Celda
        icon={Truck}
        label="Compras del período"
        value={formatCurrencyAdaptive(data.compras.monto_total)}
        hint={`${formatNumber(data.compras.total_compras)} compras · ${formatNumber(data.compras.proveedores)} proveedores`}
        tone="brand"
      />
      <Celda
        icon={Receipt}
        label="Gastos del período"
        value={formatCurrencyAdaptive(data.gastos.total_gastos)}
        hint={`Operativos ${formatCurrencyAdaptive(data.gastos.gastos_operativos, 100_000)} · Administrativos ${formatCurrencyAdaptive(data.gastos.gastos_administrativos, 100_000)}`}
        tone="amber"
      />
      <Celda
        icon={Package}
        label="Por pagar a proveedores"
        value={formatCurrencyAdaptive(data.compras.pendiente_pago)}
        hint="Saldo pendiente de las compras del período"
        tone="ink"
      />
      <Celda
        icon={Banknote}
        label="Efectivo por depositar"
        value={formatCurrencyAdaptive(data.efectivo_pendiente.total_pendiente, 100_000)}
        hint={`${formatNumber(data.efectivo_pendiente.registros)} registros`}
        tone="emerald"
      />
    </div>
  );
}
